/**
 * TikTok 三模式静态能力矩阵（与后端 TiktokShopCapabilities 对齐）。
 * 动态字段仍以 Tiktok 发品约束 GET .../publish/standard 为准（非 Shein fill-in-standard）。
 *
 * 文档不明项（默认策略，不强行猜测）：
 * 1. add-custom-attribute-value — 无模式标签：三模式先允许，失败再收敛
 * 2. /open-api/gsp/warehouse-address — 仅 FAQ：保留探测回退，不标正式能力
 * 3. query-sku-sales — 全托管默认开；半托管可选；自运营关
 * 4. stock-query 标签冲突 — 严格按三模式调用
 * 5. 全托管额度 — UI 用 check-publish-permission，不用 query-shelf-quota
 * 6. 半托管 site_list + cost_info — 按文档传；实机拒绝再收紧
 * 7. 自运营 shelf_way — Tiktok 发品页不使用 Shein 上架方式字段
 * 8. webhook：三模式已订阅事件由后端按模式增量接收并 2xx 回包
 */
import { fixedUi } from '@/i18n/fixedUi';
import i18n from '@/i18n';
import { localizedShopTypeLabel } from '@/i18n/shopTypeText';
import { normalizeTiktokShopType } from '@/utils/platformShopCatalog';

export const TIKTOK_MODE_SEMI = fixedUi("半托管");
export const TIKTOK_MODE_SELF = '自运营';
export const TIKTOK_MODE_LOCAL = '本土';
export const TIKTOK_MODE_FULL = '全托管';

export const INVENTORY_API_V2 = 'change-inventory-v2';
export const INVENTORY_API_STOCK_UPDATE = 'stock-update';

export const FINANCE_MODE_CHECK = 'checkOrder';
export const FINANCE_MODE_REPORT = 'reportOrder';

const CAPS_BY_MODE = {
  [TIKTOK_MODE_SEMI]: {
    mode: TIKTOK_MODE_SEMI,
    usesCostInfo: true,
    usesPriceInfo: false,
    canUpdateCost: false,
    canUpdateSalePrice: true,
    canDiscussCost: false,
    canShelfToggle: true,
    shelfStatusViaSpuInfoOnly: false,
    usesStopPurchase: false,
    usesShelfRequireAlways: false,
    usesPublishSiteList: true,
    warehouseList: true,
    /** 官方发品流程「二、确认站点/仓/运费」：半托必填，全托/本土跳过 */
    skipSiteWarehouseFreight: false,
    inventoryApi: INVENTORY_API_V2,
    stockQueryWarehouseType: 2,
    financeMode: FINANCE_MODE_CHECK,
    financeHasCostPrice: true,
    canReturnOrderApi: true,
    canQuerySkuSales: true,
    shelfQuotaControlled: true,
    storeNameStatusAvailable: true,
    showAfterSalesNav: true,
    showOrdersNav: true,
    showDiscussNav: true,
  },
  [TIKTOK_MODE_SELF]: {
    mode: TIKTOK_MODE_SELF,
    usesCostInfo: false,
    usesPriceInfo: true,
    canUpdateCost: false,
    canUpdateSalePrice: true,
    canDiscussCost: false,
    canShelfToggle: true,
    shelfStatusViaSpuInfoOnly: false,
    usesStopPurchase: false,
    usesShelfRequireAlways: false,
    usesPublishSiteList: true,
    warehouseList: true,
    skipSiteWarehouseFreight: false,
    inventoryApi: INVENTORY_API_V2,
    stockQueryWarehouseType: 2,
    financeMode: FINANCE_MODE_CHECK,
    financeHasCostPrice: false,
    canReturnOrderApi: true,
    canQuerySkuSales: false,
    shelfQuotaControlled: true,
    storeNameStatusAvailable: true,
    showAfterSalesNav: true,
    showOrdersNav: true,
    showDiscussNav: true,
  },
  [TIKTOK_MODE_FULL]: {
    mode: TIKTOK_MODE_FULL,
    usesCostInfo: true,
    usesPriceInfo: false,
    canUpdateCost: false,
    canUpdateSalePrice: true,
    canDiscussCost: false,
    canShelfToggle: false,
    shelfStatusViaSpuInfoOnly: true,
    usesStopPurchase: true,
    usesShelfRequireAlways: true,
    usesPublishSiteList: false,
    warehouseList: false,
    skipSiteWarehouseFreight: true,
    inventoryApi: INVENTORY_API_STOCK_UPDATE,
    stockQueryWarehouseType: 3,
    financeMode: FINANCE_MODE_REPORT,
    financeHasCostPrice: false,
    canReturnOrderApi: true,
    canQuerySkuSales: true,
    shelfQuotaControlled: false,
    storeNameStatusAvailable: false,
    showAfterSalesNav: true,
    showOrdersNav: false,
    showDiscussNav: true,
  },
};

// 本土：发品近似半托管；改价统一走 Update Price
CAPS_BY_MODE[TIKTOK_MODE_LOCAL] = {
  ...CAPS_BY_MODE[TIKTOK_MODE_SEMI],
  mode: TIKTOK_MODE_LOCAL,
  skipSiteWarehouseFreight: true,
  usesPublishSiteList: false,
  warehouseList: false,
  canDiscussCost: false,
  canUpdateCost: false,
  canUpdateSalePrice: true,
};

const EMPTY_CAPS = Object.freeze({
  mode: '',
  usesCostInfo: false,
  usesPriceInfo: false,
  canUpdateCost: false,
  canUpdateSalePrice: false,
  canDiscussCost: false,
  canShelfToggle: false,
  shelfStatusViaSpuInfoOnly: true,
  usesStopPurchase: false,
  usesShelfRequireAlways: false,
  usesPublishSiteList: false,
  warehouseList: false,
  skipSiteWarehouseFreight: false,
  inventoryApi: INVENTORY_API_V2,
  stockQueryWarehouseType: 2,
  financeMode: FINANCE_MODE_CHECK,
  financeHasCostPrice: false,
  canReturnOrderApi: false,
  canQuerySkuSales: false,
  shelfQuotaControlled: false,
  storeNameStatusAvailable: false,
  showAfterSalesNav: false,
  showOrdersNav: false,
  showDiscussNav: false,
});

export function resolveTiktokShopMode(shopType) {
  return normalizeTiktokShopType(shopType) || '';
}

export function tiktokShopCapabilities(shopTypeOrShop) {
  const shopType = typeof shopTypeOrShop === 'object' && shopTypeOrShop != null
    ? shopTypeOrShop.shopType
    : shopTypeOrShop;
  const mode = resolveTiktokShopMode(shopType);
  if (!mode) {
    // 未知类型：返回空能力常量（勿每次 new object，避免 effect 依赖抖）
    return EMPTY_CAPS;
  }
  return CAPS_BY_MODE[mode] || CAPS_BY_MODE[TIKTOK_MODE_SEMI];
}

export function workspaceSubtitleForShopType(shopType) {
  const mode = resolveTiktokShopMode(shopType);
  if (mode) {
    const modeLabel = localizedShopTypeLabel(
      (key, opts) => i18n.t(key, { ns: 'workbench', ...opts }),
      mode,
    );
    return i18n.t('platformBar.subtitleMode', {
      ns: 'workbench',
      mode: modeLabel,
      defaultValue: `${modeLabel}工作区`,
    });
  }
  return i18n.t('platformBar.subtitleFallback', { ns: 'workbench', platform: 'TikTok', defaultValue: 'TikTok 工作区' });
}

/** @deprecated 门禁文案以 tiktokGateCopy.js 为准；此处仅作兼容转发。 */
export function gateCopyForShopType(shopType) {
  // 延迟加载，避免与 tiktokGateCopy ↔ capabilities 循环依赖
  // eslint-disable-next-line global-require, import/no-dynamic-require
  const { gateCopyForTiktokShopType } = require('@/tiktok-platform/utils/tiktokGateCopy');
  return gateCopyForTiktokShopType(shopType);
}
